import type { CSSProperties } from 'react';
import { UTCDate } from '@date-fns/utc';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import css from './date-time-picker.module.scss';

const styles = pickCss(css, css.root);

type DateTimePickerProps = {
  /** UTC ISO string, or undefined when unset. */
  value?: string;
  onChange: (iso: string | undefined) => void;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  id?: string;
  /**
   * Earliest selectable date as `yyyy-MM-dd`. Sets the native date input's
   * `min` so past days are greyed out in the calendar. Schema validation
   * remains the source of truth (a `min` only constrains the day, not the
   * time, and can be bypassed).
   */
  minDate?: string;
};

// Split a UTC ISO string into the `yyyy-MM-dd` and `HH:mm:ss` parts the
// two native inputs bind to. Empty strings when the value is unset.
const splitIso = (iso?: string): { date: string; time: string } => {
  if (!iso) return { date: '', time: '' };
  const d = new UTCDate(iso);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(
      d.getUTCDate()
    )}`,
    time: `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(
      d.getUTCSeconds()
    )}`,
  };
};

// Recombine the date + time parts into a single UTC ISO string. Returns
// undefined when no date is selected (time alone is meaningless).
const joinIso = (date: string, time: string): string | undefined => {
  if (!date) return undefined;
  const t = time || '00:00:00';
  const normalized = t.length === 5 ? `${t}:00` : t;
  const d = new UTCDate(`${date}T${normalized}Z`);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
};

/**
 * Date + time picker that emits a single UTC ISO string. Matches the
 * legacy task form's "Expire On" widget — a "Pick a date" field next to a
 * separate time field. Both are native inputs (the codebase convention,
 * see `DateRangePicker`); all parsing goes through `UTCDate` so the value
 * is timezone-stable end-to-end.
 */
export const DateTimePicker = ({
  value,
  onChange,
  disabled,
  className,
  style,
  id,
  minDate,
}: DateTimePickerProps) => {
  const { date, time } = splitIso(value);

  return (
    <div
      className={styles({ isDisabled: !!disabled }, className)}
      style={style}
    >
      <input
        aria-label="Date"
        className={joinCss(css.field, css.date)}
        disabled={disabled}
        id={id}
        min={minDate}
        onChange={(e) => onChange(joinIso(e.target.value, time))}
        type="date"
        value={date}
      />
      <input
        aria-label="Time"
        className={joinCss(css.field, css.time)}
        disabled={disabled}
        onChange={(e) => onChange(joinIso(date, e.target.value))}
        step={1}
        type="time"
        value={time}
      />
    </div>
  );
};
